import { Store } from '@tanstack/store'

/**
 * App state interface
 */
export interface AppState {
  bookmarkBannerDismissed: boolean
  extensionPromptDismissed: boolean
}

/**
 * Default app state
 */
const defaultState: AppState = {
  bookmarkBannerDismissed: false,
  extensionPromptDismissed: false,
}

/**
 * Load state from localStorage
 */
function loadStateFromLocalStorage(): AppState {
  if (typeof window === 'undefined') {
    return defaultState
  }

  try {
    const stored = localStorage.getItem('app-state')
    if (stored) {
      const parsed = JSON.parse(stored)
      return { ...defaultState, ...parsed }
    }
  } catch (error) {
    console.error('Failed to load state from localStorage:', error)
  }

  return defaultState
}

/**
 * Save state to localStorage
 */
function saveStateToLocalStorage(state: AppState) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    localStorage.setItem('app-state', JSON.stringify(state))
  } catch (error) {
    console.error('Failed to save state to localStorage:', error)
  }
}

/**
 * Create the app store with localStorage persistence
 */
export const appStore = new Store<AppState>(loadStateFromLocalStorage())

/**
 * Subscribe to state changes and persist to localStorage
 */
appStore.subscribe(() => {
  saveStateToLocalStorage(appStore.state)
})

/**
 * Action: Dismiss bookmark banner
 */
export function dismissBookmarkBanner() {
  appStore.setState((state) => ({
    ...state,
    bookmarkBannerDismissed: true,
  }))
}

/**
 * Action: Reset bookmark banner (for testing)
 */
export function resetBookmarkBanner() {
  appStore.setState((state) => ({
    ...state,
    bookmarkBannerDismissed: false,
  }))
}

/**
 * Action: Dismiss extension prompt
 */
export function dismissExtensionPrompt() {
  appStore.setState((state) => ({
    ...state,
    extensionPromptDismissed: true,
  }))
}

/**
 * Action: Reset extension prompt (for testing)
 */
export function resetExtensionPrompt() {
  appStore.setState((state) => ({
    ...state,
    extensionPromptDismissed: false,
  }))
}
